import type { AxiosError } from "axios";
import i18n from "../i18n";

/** Standardized API error response format (from backend 6-4-1).
 * `code`/`params` are optional additions from Story 11.7-1 (AppError mechanism) —
 * only present when the backend call site has been migrated. */
interface StandardApiError {
  message: string;
  field?: string;
  code?: string;
  params?: Record<string, string>;
}

/** Multiple validation errors format. */
interface MultipleApiErrors {
  message: string;
  errors: Array<{ message: string; field?: string }>;
}

/** Legacy Pydantic/FastAPI validation error item (422) - fallback only. */
interface ValidationErrorItem {
  type?: string;
  loc?: (string | number)[];
  msg?: string;
  ctx?: Record<string, unknown>;
}

/** API error response body shape. */
type ApiErrorResponse =
  | StandardApiError
  | MultipleApiErrors
  | { detail?: string | ValidationErrorItem[]; message?: string }
  | { message?: string };

/**
 * Extract a user-friendly message from an API error (e.g. Axios 422/4xx).
 * Prefers standardized `message` field (from backend 6-4-1), falls back to legacy `detail` for backward compatibility.
 */
export function getApiErrorMessage(error: unknown, fallback = "An unexpected error occurred. Please try again."): string {
  if (error == null) return fallback;

  const ax = error as AxiosError<ApiErrorResponse>;
  const data = ax.response?.data;

  if (data == null) {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }

  // Story 11.7-1: structured error code (AppError) — translated + interpolated when
  // recognized by this frontend build. The `i18n.exists()` guard is mandatory: an
  // unrecognized/unmigrated code must fall through to the raw `message` below, never
  // render the literal `errors:<code>` key (the exact bug class from Story 11.6-1).
  if ("code" in data && typeof data.code === "string" && data.code.trim() && i18n.exists(`errors:${data.code}`)) {
    return i18n.t(`errors:${data.code}`, data.params ?? {});
  }

  // Prefer standardized `message` field (backend 6-4-1)
  if ("message" in data && typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }

  // Support multiple errors format: return summary message
  if ("errors" in data && Array.isArray(data.errors) && data.errors.length > 0) {
    // Return the summary message if available, otherwise construct from errors
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }
    // Fallback: use first error message
    const firstError = data.errors[0];
    if (firstError && typeof firstError.message === "string") {
      return firstError.message;
    }
  }

  // Legacy fallback: handle `detail` field (for old endpoints or proxies)
  const detail = "detail" in data ? data.detail : undefined;

  if (detail == null) {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail) && detail.length > 0) {
    // Map legacy Pydantic detail array to user-friendly string
    const first = detail[0] as ValidationErrorItem;
    const msg = first?.msg ?? "Validation errors occurred";
    const loc = first?.loc;
    if (loc && Array.isArray(loc) && loc.length > 0) {
      const field = loc[loc.length - 1];
      return `${field}: ${msg}`;
    }
    return msg;
  }

  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Extract full API error payload for components that need to display multiple errors.
 * Returns standardized format with optional field and errors array.
 * 
 * **Usage in Forms:**
 * ```typescript
 * try {
 *   await submitForm(data);
 * } catch (error) {
 *   const payload = getApiErrorPayload(error);
 *   if (payload.errors && payload.errors.length > 0) {
 *     // Map field-level errors to form fields
 *     payload.errors.forEach(err => {
 *       if (err.field) {
 *         setError(err.field, { message: err.message });
 *       }
 *     });
 *   } else {
 *     // Show summary message
 *     showToast(payload.message);
 *   }
 * }
 * ```
 */
export function getApiErrorPayload(
  error: unknown
): { message: string; field?: string; errors?: Array<{ message: string; field?: string }> } {
  if (error == null) {
    return { message: "An unexpected error occurred. Please try again." };
  }

  const ax = error as AxiosError<ApiErrorResponse>;
  const data = ax.response?.data;

  if (data == null) {
    if (error instanceof Error && error.message) {
      return { message: error.message };
    }
    return { message: "An unexpected error occurred. Please try again." };
  }

  // Standardized format: single error with message (and optional field)
  if ("message" in data && typeof data.message === "string" && data.message.trim()) {
    const result: { message: string; field?: string; errors?: Array<{ message: string; field?: string }> } = {
      message: data.message,
    };
    if ("field" in data && typeof data.field === "string") {
      result.field = data.field;
    }
    // If multiple errors format, include errors array
    if ("errors" in data && Array.isArray(data.errors) && data.errors.length > 0) {
      result.errors = data.errors.map((e) => ({
        message: e.message || "Validation error",
        field: e.field,
      }));
    }
    return result;
  }

  // Legacy fallback: map detail to message
  const detail = "detail" in data ? data.detail : undefined;
  if (typeof detail === "string") {
    return { message: detail };
  }

  if (Array.isArray(detail) && detail.length > 0) {
    // Map legacy Pydantic array to standardized format
    const errors = detail.map((item: ValidationErrorItem) => {
      const msg = item?.msg ?? "Validation error";
      const loc = item?.loc;
      let field: string | undefined;
      if (loc && Array.isArray(loc) && loc.length > 0) {
        const fieldName = loc[loc.length - 1];
        field = String(fieldName);
      }
      return { message: msg, field };
    });
    return {
      message: errors.length === 1 ? errors[0].message : "Please correct the following errors",
      errors,
    };
  }

  if (error instanceof Error && error.message) {
    return { message: error.message };
  }

  return { message: "An unexpected error occurred. Please try again." };
}
